import { Children, useId, useState } from 'react'
import type { ComponentType, FormEvent, ReactNode, SVGProps } from 'react'
import { cn } from '@/lib/cn'
import { CheckCircleIcon, ChevronDownIcon, ChevronRightIcon } from '@/components/ui/icons'
import { Button } from '@/components/ui/Button'

interface EditableRowProps {
  icon: ComponentType<SVGProps<SVGSVGElement>>
  /** Valor atual. Ausente = campo ainda não preenchido. */
  value?: string | null
  /** Texto de apoio abaixo do valor, ex.: "Nome de preferência". */
  description: string
  /** Marca verde de "informação validada". */
  verified?: boolean
  /** Formulário exibido ao expandir a linha. */
  children: ReactNode
  /** O valor devolvido é ignorado — a linha só precisa saber quando terminou. */
  onSubmit: () => Promise<unknown> | void
  submitLabel?: string
  submitting?: boolean
  /** Desativa o botão quando o formulário ainda não está válido. */
  canSubmit?: boolean
}

/**
 * Linha expansível de "Dados pessoais".
 *
 * Fechada mostra valor + descrição + seta; aberta revela os campos e o botão
 * "Atualizar", como nas telas de referência.
 */
export function EditableRow({
  icon: Icon,
  value,
  description,
  verified = true,
  children,
  onSubmit,
  submitLabel = 'Atualizar',
  submitting,
  canSubmit = true,
}: EditableRowProps) {
  const [open, setOpen] = useState(false)
  const panelId = useId()

  // O último campo é o que divide a linha com o botão; os anteriores ganham a
  // linha inteira no celular. No desktop os invólucros somem (`md:contents`) e
  // tudo volta a ser uma fileira só.
  const fields = Children.toArray(children)
  const lastField = fields.pop()

  async function handleSubmit(event: FormEvent) {
    event.preventDefault()
    await onSubmit()
    setOpen(false)
  }

  return (
    <div className="border-b border-line last:border-b-0">
      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        aria-expanded={open}
        aria-controls={panelId}
        className="flex w-full items-center gap-3 px-5 py-3.5 text-left transition-colors hover:bg-surface-muted"
      >
        <div className="min-w-0 flex-1">
          <span className="flex items-center gap-2">
            <Icon className="size-[18px] shrink-0 text-ink-soft" />
            <span
              className={cn(
                'truncate text-[15px]',
                value ? 'text-ink' : 'text-ink-faint',
              )}
            >
              {value || 'Não informado'}
            </span>
          </span>
          <span className="mt-0.5 block text-[13px] text-ink-soft">{description}</span>
        </div>

        {verified && value && (
          <CheckCircleIcon className="size-[18px] shrink-0 text-brand" aria-label="Informação confirmada" />
        )}
        {open ? (
          <ChevronDownIcon className="size-5 shrink-0 text-ink-soft" />
        ) : (
          <ChevronRightIcon className="size-5 shrink-0 text-ink-soft" />
        )}
      </button>

      {open && (
        // No celular o formulário encosta na borda do card (sem o recuo que
        // alinha com o texto no desktop) para o campo e o botão caberem lado a
        // lado; o rótulo do botão encolhe para "OK" pelo mesmo motivo.
        <form
          id={panelId}
          onSubmit={handleSubmit}
          className="flex flex-wrap items-end gap-3 px-5 pb-5 animate-[slide-down_0.15s_ease-out] md:pl-12"
        >
          {fields.map((field, index) => (
            <div key={index} className="w-full md:contents">
              {field}
            </div>
          ))}
          {lastField}
          <Button
            type="submit"
            loading={submitting}
            disabled={!canSubmit}
            className="h-11 shrink-0"
          >
            <span className="md:hidden">OK</span>
            <span className="hidden md:inline">{submitLabel}</span>
          </Button>
        </form>
      )}
    </div>
  )
}
